import { useEffect, useState } from "react";
import {
  adminListExperiments,
  adminCreateExperiment,
  adminUpdateExperiment,
  adminDeleteExperiment,
  adminExperimentResults,
  adminSuggestVariants,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { Beaker, Play, Pause, Archive, Trash2, BarChart2, Plus, Check, Sparkles, Loader2, Zap, Users, Heart } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const STATUS_STYLES = {
  active: "bg-emerald-100 text-emerald-800",
  draft: "bg-stone-100 text-stone-700",
  archived: "bg-stone-100 text-stone-500 line-through",
};

/**
 * Admin page for slot-based A/B experiments. List + create + inline
 * activate/archive/delete + open results dialog with 2-proportion z-test.
 */
export default function Experiments() {
  const { user } = useAuth();
  const isAdmin = user?.role === "Admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState(null);  // full results doc

  const load = async () => {
    setLoading(true);
    try { setItems(await adminListExperiments()); }
    catch { toast.error("Could not load experiments"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const patch = async (eid, body) => {
    try {
      await adminUpdateExperiment(eid, body);
      toast.success("Saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };
  const openResults = async (eid) => {
    try { setViewing(await adminExperimentResults(eid)); }
    catch { toast.error("Could not load results"); }
  };
  const remove = async (eid) => {
    if (!confirm("Delete this experiment and all its events? This cannot be undone.")) return;
    try {
      await adminDeleteExperiment(eid);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div data-testid="admin-experiments-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-wider text-ccc-purple">A/B testing</div>
          <h1 className="font-serif text-4xl text-ccc-plum">Experiments.</h1>
          <p className="text-sm text-stone-600 mt-1">Test copy, layouts, and CTAs on the public site.</p>
        </div>
        <Button onClick={() => setCreating(true)} data-testid="experiments-new-btn">
          <Plus size={14} className="mr-1"/> New experiment
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-stone-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-stone-200 p-12 text-center" data-testid="experiments-empty">
          <Beaker className="mx-auto text-stone-400" size={32}/>
          <div className="font-serif text-xl text-ccc-plum mt-3">No experiments yet.</div>
          <p className="text-sm text-stone-500 mt-1">
            Create one to test different copy or CTAs on the public site.
          </p>
        </div>
      ) : (
        <div className="bg-white border border-stone-200 divide-y divide-stone-100">
          {items.map(exp => (
            <ExperimentRow
              key={exp.id}
              exp={exp}
              isAdmin={isAdmin}
              onPatch={patch}
              onOpen={() => openResults(exp.id)}
              onDelete={() => remove(exp.id)}
            />
          ))}
        </div>
      )}

      <CreateExperimentDialog
        open={creating}
        onOpenChange={setCreating}
        onCreated={() => { setCreating(false); load(); }}
      />

      <ResultsDialog
        results={viewing}
        onOpenChange={(open) => { if (!open) setViewing(null); }}
      />
    </div>
  );
}

function ExperimentRow({ exp, isAdmin, onPatch, onOpen, onDelete }) {
  const badge = STATUS_STYLES[exp.status] || STATUS_STYLES.draft;
  let when = exp.created_at;
  try { when = formatDistanceToNow(new Date(exp.created_at), { addSuffix: true }); } catch { /* ignore */ }
  return (
    <div className="p-4 flex items-center gap-4" data-testid={`exp-row-${exp.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 ${badge}`} data-testid={`exp-status-${exp.id}`}>
            {exp.status}
          </span>
          <span className="text-xs text-stone-500 font-mono">{exp.slot}</span>
        </div>
        <div className="font-serif text-lg text-ccc-plum mt-1">{exp.name}</div>
        <div className="text-xs text-stone-500">
          {exp.variants.length} variants · created {when}
          {exp.description ? ` · ${exp.description.slice(0, 80)}` : ""}
        </div>
      </div>
      <div className="flex items-center gap-1">
        <Button variant="outline" size="sm" onClick={onOpen} data-testid={`exp-results-${exp.id}`}>
          <BarChart2 size={14} className="mr-1"/> Results
        </Button>
        {exp.status !== "active" && (
          <Button variant="outline" size="sm" onClick={() => onPatch(exp.id, { status: "active" })}
                  data-testid={`exp-activate-${exp.id}`}>
            <Play size={14} className="mr-1"/> Activate
          </Button>
        )}
        {exp.status === "active" && (
          <Button variant="outline" size="sm" onClick={() => onPatch(exp.id, { status: "draft" })}
                  data-testid={`exp-pause-${exp.id}`}>
            <Pause size={14} className="mr-1"/> Pause
          </Button>
        )}
        {exp.status !== "archived" && (
          <Button variant="outline" size="sm" onClick={() => onPatch(exp.id, { status: "archived" })}
                  data-testid={`exp-archive-${exp.id}`}>
            <Archive size={14} className="mr-1"/> Archive
          </Button>
        )}
        {isAdmin && (
          <Button variant="outline" size="sm" onClick={onDelete}
                  data-testid={`exp-delete-${exp.id}`}
                  className="text-red-600 hover:text-red-700">
            <Trash2 size={14}/>
          </Button>
        )}
      </div>
    </div>
  );
}

function CreateExperimentDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({
    slot: "home_hero_cta", name: "", description: "",
    conversion_event: "click",
    variant_a_label: "Baseline (A)", variant_a_text: "Join the Network",
    variant_b_label: "Variant B", variant_b_text: "Take Action Today",
    status: "draft",
  });
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  useEffect(() => {
    if (open) {
      setForm(f => ({ ...f, name: "", description: "" }));
      setSuggestions(null);
    }
  }, [open]);

  const suggest = async () => {
    if (!form.variant_a_text.trim()) {
      toast.error("Enter baseline (variant A) text first"); return;
    }
    setSuggesting(true);
    setSuggestions(null);
    try {
      const r = await adminSuggestVariants({
        slot: form.slot,
        baseline_text: form.variant_a_text,
        context: form.description,
      });
      setSuggestions(r.suggestions);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI suggestion failed");
    } finally {
      setSuggesting(false);
    }
  };

  const pickSuggestion = (s) => {
    setForm(f => ({ ...f, variant_b_label: s.label, variant_b_text: s.text }));
    toast.success(`Applied ${s.hook} variant`);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    if (!form.slot.trim()) { toast.error("Slot required"); return; }
    setSaving(true);
    try {
      await adminCreateExperiment({
        slot: form.slot.trim(),
        name: form.name.trim(),
        description: form.description.trim(),
        status: form.status,
        conversion_event: form.conversion_event,
        variants: [
          { key: "A", label: form.variant_a_label, content: { text: form.variant_a_text }, weight: 50 },
          { key: "B", label: form.variant_b_label, content: { text: form.variant_b_text }, weight: 50 },
        ],
      });
      toast.success("Created");
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="exp-create-dialog">
        <DialogHeader>
          <DialogTitle>New experiment</DialogTitle>
          <DialogDescription>
            Two-variant A/B on a public-site slot. Only one experiment per slot can be active at a time.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="exp-slot">Slot</Label>
              <Input id="exp-slot" value={form.slot}
                     onChange={(e) => setForm({ ...form, slot: e.target.value })}
                     placeholder="home_hero_cta"
                     data-testid="exp-slot-input"/>
              <p className="text-xs text-stone-500 mt-1">Well-known: <code>home_hero_cta</code></p>
            </div>
            <div>
              <Label htmlFor="exp-conv">Conversion event</Label>
              <Input id="exp-conv" value={form.conversion_event}
                     onChange={(e) => setForm({ ...form, conversion_event: e.target.value })}
                     data-testid="exp-conv-input"/>
              <p className="text-xs text-stone-500 mt-1">Free text — describe the click/signup we count.</p>
            </div>
          </div>
          <div>
            <Label htmlFor="exp-name">Name</Label>
            <Input id="exp-name" value={form.name}
                   onChange={(e) => setForm({ ...form, name: e.target.value })}
                   placeholder="Homepage Hero CTA — urgency vs baseline"
                   data-testid="exp-name-input"/>
          </div>
          <div>
            <Label htmlFor="exp-desc">Description</Label>
            <Input id="exp-desc" value={form.description}
                   onChange={(e) => setForm({ ...form, description: e.target.value })}
                   placeholder="Optional — helps AI suggester understand the slot"
                   data-testid="exp-desc-input"/>
          </div>
          <div className="border rounded p-3 bg-stone-50">
            <div className="text-xs uppercase tracking-wider text-stone-500 mb-2">Variant A · Baseline</div>
            <Input placeholder="Label" value={form.variant_a_label}
                   onChange={(e) => setForm({ ...form, variant_a_label: e.target.value })}
                   className="mb-2"
                   data-testid="exp-a-label"/>
            <Input placeholder="Text content shown on the site" value={form.variant_a_text}
                   onChange={(e) => setForm({ ...form, variant_a_text: e.target.value })}
                   data-testid="exp-a-text"/>
          </div>
          <div className="border rounded p-3 bg-stone-50">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-wider text-stone-500">Variant B · Challenger</div>
              <Button
                type="button" variant="outline" size="sm"
                onClick={suggest} disabled={suggesting || !form.variant_a_text.trim()}
                className="text-ccc-purple border-ccc-purple/40 hover:bg-ccc-purple/5"
                data-testid="exp-suggest-btn"
              >
                {suggesting ? <><Loader2 size={12} className="mr-1 animate-spin"/> Thinking…</>
                            : <><Sparkles size={12} className="mr-1"/> Suggest with AI</>}
              </Button>
            </div>
            <Input placeholder="Label" value={form.variant_b_label}
                   onChange={(e) => setForm({ ...form, variant_b_label: e.target.value })}
                   className="mb-2"
                   data-testid="exp-b-label"/>
            <Input placeholder="Text content shown on the site" value={form.variant_b_text}
                   onChange={(e) => setForm({ ...form, variant_b_text: e.target.value })}
                   data-testid="exp-b-text"/>
            {suggestions && (
              <div className="mt-3 space-y-2" data-testid="exp-suggestions-list">
                <div className="text-[10px] uppercase tracking-wider text-ccc-purple">
                  Pick one — it fills the variant B fields above:
                </div>
                {suggestions.map(s => <SuggestionCard key={s.hook} s={s} onPick={pickSuggestion}/>)}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 text-sm">
            <input type="checkbox" id="exp-active"
                   checked={form.status === "active"}
                   onChange={(e) => setForm({ ...form, status: e.target.checked ? "active" : "draft" })}
                   data-testid="exp-active-check"/>
            <Label htmlFor="exp-active" className="cursor-pointer">Activate immediately</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}
                  data-testid="exp-cancel-btn">
            Cancel
          </Button>
          <Button onClick={save} disabled={saving} data-testid="exp-save-btn">
            {saving ? "Saving…" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SuggestionCard({ s, onPick }) {
  const HOOK_ICONS = {
    urgency: <Zap size={12} className="text-amber-600"/>,
    community: <Users size={12} className="text-emerald-600"/>,
    values: <Heart size={12} className="text-rose-600"/>,
  };
  const HOOK_TINTS = {
    urgency: "border-amber-200 bg-amber-50/50",
    community: "border-emerald-200 bg-emerald-50/50",
    values: "border-rose-200 bg-rose-50/50",
  };
  return (
    <div className={`border ${HOOK_TINTS[s.hook] || "border-stone-200"} p-2.5 flex items-start gap-3`}
         data-testid={`exp-suggestion-${s.hook}`}>
      <div className="flex flex-col items-center gap-1 pt-1">
        {HOOK_ICONS[s.hook]}
        <span className="text-[9px] uppercase tracking-wider text-stone-500">{s.hook}</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-ccc-plum">{s.label}</div>
        <div className="text-sm text-stone-700 mt-0.5">"{s.text}"</div>
        {s.rationale && (
          <div className="text-xs text-stone-500 italic mt-1">{s.rationale}</div>
        )}
      </div>
      <Button size="sm" variant="outline" onClick={() => onPick(s)}
              className="shrink-0"
              data-testid={`exp-pick-${s.hook}`}>
        Use
      </Button>
    </div>
  );
}

function ResultsDialog({ results, onOpenChange }) {
  const open = !!results;
  if (!results) {
    return (
      <Dialog open={false} onOpenChange={onOpenChange}>
        <DialogContent><DialogHeader><DialogTitle/></DialogHeader></DialogContent>
      </Dialog>
    );
  }
  const { experiment, variants, z_test, winner } = results;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="exp-results-dialog">
        <DialogHeader>
          <DialogTitle>Results · {experiment.name}</DialogTitle>
          <DialogDescription>
            Slot <code>{experiment.slot}</code> · status {experiment.status} · conversion event: {experiment.conversion_event}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {winner ? (
            <div className="border border-emerald-300 bg-emerald-50 p-3 flex items-center gap-3" data-testid="exp-winner-banner">
              <Check className="text-emerald-600" size={20}/>
              <div className="text-sm">
                <div className="font-semibold text-emerald-800">Variant {winner} wins</div>
                <div className="text-emerald-700">
                  Statistically significant at {z_test?.confidence || "95%"} (z = {z_test?.z}, p = {z_test?.p_value}).
                </div>
              </div>
            </div>
          ) : z_test ? (
            <div className="border border-amber-300 bg-amber-50 p-3 text-sm" data-testid="exp-inconclusive-banner">
              <div className="font-semibold text-amber-800">No significant winner yet</div>
              <div className="text-amber-700">z = {z_test.z}, p = {z_test.p_value} — needs a larger sample or more time.</div>
            </div>
          ) : (
            <div className="border border-stone-300 bg-stone-50 p-3 text-sm" data-testid="exp-small-sample-banner">
              <div className="font-semibold text-stone-800">Sample too small</div>
              <div className="text-stone-600">Each variant needs at least 30 unique visitors before we run the z-test.</div>
            </div>
          )}
          <div className="border border-stone-200 divide-y divide-stone-100">
            <div className="grid grid-cols-6 gap-2 px-3 py-2 text-[10px] uppercase tracking-wider text-stone-500 bg-stone-50">
              <div>Variant</div>
              <div className="col-span-2">Content</div>
              <div className="text-right">Impressions</div>
              <div className="text-right">Conversions</div>
              <div className="text-right">Rate</div>
            </div>
            {variants.map(v => (
              <div key={v.key} className="grid grid-cols-6 gap-2 px-3 py-2 text-sm items-center"
                   data-testid={`exp-variant-row-${v.key}`}>
                <div>
                  <span className="font-mono font-semibold">{v.key}</span>
                  <span className="text-xs text-stone-500 ml-2">{v.label}</span>
                </div>
                <div className="col-span-2 text-xs text-stone-700 truncate">{v.content?.text || "—"}</div>
                <div className="text-right font-mono">{v.unique_impressions}</div>
                <div className="text-right font-mono">{v.unique_conversions}</div>
                <div className="text-right font-mono font-semibold" data-testid={`exp-variant-rate-${v.key}`}>
                  {v.conversion_rate_pct}%
                </div>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
